import { Alert, Empty, Modal, Radio, Spin, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { format } from "date-fns";
import _ from "lodash";
import React, { useState } from "react";
import {
  Scope,
  useCanDeleteBatchesQuery,
  useGetBatchesQuery,
  useUpdateBatchMutation,
} from "../../generated/types";
import { Batch } from "../../types";
import { InputWithAsyncConfirm } from "../InputWithAsyncConfirm/InputWithAsyncConfirm";
import { DeleteBatchButton, EXTERNAL_DELETE_NOTE } from "./DeleteBatchButton";
import { EDIT_BATCHES_TXT } from "./EditBatchesButton";
import styles from "./styles.module.css";

const modalMsg = (scope: Scope) => (
  <ul className={styles["edit-batches-alert-msg"]}>
    {scope === Scope.Internal && (
      <>
        <li>
          You need the <b>Delivery Core Projects Config</b> permission in Janus
          to edit week numbers
        </li>
        <li>
          If you edit an old week number, subsequent week numbers will NOT be
          updated
        </li>
      </>
    )}
    <li>The project start date does NOT affect the week numbers shown here</li>
    {scope === Scope.External ? (
      <li>{EXTERNAL_DELETE_NOTE}</li>
    ) : (
      <li>
        Updates here only go to <b>internal</b>, NOT <b>external</b>. You will
        need to release this project if you want the changes to go to external
      </li>
    )}
  </ul>
);

type BatchTableDataRow = {
  key: string;
  weekNumber: Batch;
  timestamp: Batch;
};

type Props = {
  customer: string;
  project: string;
  visible: boolean;
  setVisible: (vis: boolean) => void;
};

export const EditBatchesModal = (props: Props) => {
  const { customer, project, visible, setVisible } = props;

  const [scope, setScope] = useState<Scope>(Scope.Internal);

  const { data: permData, loading: permLoading } = useCanDeleteBatchesQuery({
    skip: !visible,
  });
  const canDelete = !permLoading && permData?.canDeleteBatches === true;

  const {
    data: batchesData,
    loading: batchesLoading,
    error: batchesError,
    refetch,
  } = useGetBatchesQuery({
    variables: { customer, project, scope },
    skip: visible === false,
    // Internal and external batches share ids and Batch has no type policy, so any cached write
    // would put one scope's rows into the shared Batch:<id> entries that the batch selectors read later.
    // The proper fix is a Batch type policy with keyFields: false, tracked separately.
    fetchPolicy: "no-cache",
  });

  const batchesTableData: BatchTableDataRow[] = _(batchesData?.batches ?? [])
    .map((batch) => ({
      key: batch.id,
      weekNumber: batch,
      timestamp: batch,
    }))
    .sortBy((b) => b.timestamp)
    .value();

  const [updateBatch] = useUpdateBatchMutation({
    onCompleted: () => {
      refetch().catch(() => undefined);
    },
  });

  const handleOnUpdateWeekNumber = async (
    batch: Batch,
    newWeekNumber: number,
  ) => {
    await updateBatch({
      variables: {
        customer,
        project,
        batchId: batch.id,
        timestamp: batch.timestamp,
        weekNumber: newWeekNumber,
      },
    });
  };

  const batchesTableColumns: ColumnsType<BatchTableDataRow> = [
    {
      title: "Week Number",
      dataIndex: "weekNumber",
      key: "weekNumber",
      width: "200px",
      render: (batch: Batch) =>
        scope === Scope.External ? (
          batch.weekNumber
        ) : (
          <InputWithAsyncConfirm<number>
            value={batch.weekNumber}
            confirmButtonText={"ok"}
            confirmButtonPositionLeft={true}
            onConfirm={(newWeekNumber: number) =>
              handleOnUpdateWeekNumber(batch, newWeekNumber)
            }
          />
        ),
    },
    {
      title: "Timestamp",
      dataIndex: "timestamp",
      key: "timestamp",
      render: (batch: Batch) => format(new Date(batch.timestamp), "yyyy/MM/dd"),
    },
    ...(canDelete
      ? [
          {
            title: "Actions",
            dataIndex: "timestamp",
            key: "actions",
            render: (batch: Batch) => (
              <DeleteBatchButton
                customer={customer}
                project={project}
                scope={scope}
                batch={batch}
                onDeleted={() => refetch().catch(() => undefined)}
              />
            ),
          },
        ]
      : []),
  ];

  const batchesTable = (
    <Table
      dataSource={batchesTableData}
      columns={batchesTableColumns}
      pagination={false}
      size="small"
      sticky
    />
  );

  const modalTitle = `${EDIT_BATCHES_TXT}: ${customer}-${project}`;

  return (
    <>
      <Modal
        visible={visible}
        onCancel={() => {
          setVisible(false);
          setScope(Scope.Internal);
        }}
        title={modalTitle}
        footer={false}
      >
        <Alert
          className={styles["edit-batches-alert"]}
          showIcon={true}
          type={"info"}
          message={modalMsg(scope)}
        />
        {canDelete && (
          <Radio.Group
            data-testid="batch-scope-toggle"
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            options={[
              { label: "Internal", value: Scope.Internal },
              { label: "External", value: Scope.External },
            ]}
          />
        )}
        {batchesLoading ? (
          <Spin />
        ) : batchesError ? (
          <Alert type="error" showIcon message={batchesError.message} />
        ) : batchesTableData?.length === 0 ? (
          <Empty />
        ) : (
          batchesTable
        )}
      </Modal>
    </>
  );
};
